import React, { useState } from 'react';
import { Modal, Stack, TextInput, MultiSelect, Group, Button, Alert } from '@mantine/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { GradeEditModal } from './GradeEditModal';
import { SwimlaneEditModal } from './SwimlaneEditModal';
import { OrderedMultiSelect } from '../ui/OrderedMultiSelect';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';
import { CompetencyMap, Grade, Swimlane, Profession } from '../../types';

interface CompetencyMapModalProps {
  opened: boolean;
  onClose: () => void;
  editMap: CompetencyMap | null;
}

// Выбранные элементы в прежнем порядке, новые — в конец.
function keepOrder<T extends { _id: string }>(current: T[], all: T[], selectedIds: string[]): T[] {
  const kept = current.filter((item) => selectedIds.includes(item._id));
  const added = selectedIds
    .filter((id) => !kept.some((item) => item._id === id))
    .map((id) => all.find((item) => item._id === id))
    .filter((item): item is T => !!item);
  return [...kept, ...added];
}

export const CompetencyMapModal: React.FC<CompetencyMapModalProps> = ({
  opened,
  onClose,
  editMap,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState('');
  const [selectedProfessions, setSelectedProfessions] = useState<string[]>([]);
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [gradesOrder, setGradesOrder] = useState<Grade[]>([]);
  const [selectedSwimlanes, setSelectedSwimlanes] = useState<string[]>([]);
  const [swimlanesOrder, setSwimlanesOrder] = useState<Swimlane[]>([]);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [swimlaneModalOpen, setSwimlaneModalOpen] = useState(false);
  const [selectedGradeForEdit, setSelectedGradeForEdit] = useState<Grade | null>(null);
  const [selectedSwimlaneForEdit, setSelectedSwimlaneForEdit] = useState<Swimlane | null>(null);

  // Получение профессий для селекта
  const { data: professions = [] } = useQuery({
    queryKey: ['professions'],
    queryFn: async () => api('/professions'),
    staleTime: 5 * 60 * 1000,
  });

  // Получение грейдов для селекта
  const { data: grades = [] } = useQuery({
    queryKey: ['grades'],
    queryFn: async () => api('/grades'),
    staleTime: 5 * 60 * 1000,
  });

  // Получение свимлейнов для селекта
  const { data: swimlanes = [] } = useQuery({
    queryKey: ['swimlanes'],
    queryFn: async () => api('/swimlanes'),
    staleTime: 5 * 60 * 1000,
  });

  // Мутация создания
  const createMapMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      professions: string[];
      grades: string[];
      swimlanes: string[];
    }) => api('/competency-maps', { method: 'POST', body: data }),
    onSuccess: () => {
      showToast({
        title: t('messages.created', { item: t('competencyMaps.title') }),
        message: '',
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['competency-maps'] });
      onClose();
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        t('messages.errorCreating', { item: t('competencyMaps.title') }),
      );
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  // Мутация редактирования
  const editMapMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      name: string;
      professions: string[];
      grades: string[];
      swimlanes: string[];
    }) =>
      api(`/competency-maps/${data.id}`, {
        method: 'PATCH',
        body: {
          name: data.name,
          professions: data.professions,
          grades: data.grades,
          swimlanes: data.swimlanes,
        },
      }),
    onSuccess: () => {
      showToast({
        title: t('messages.updated', { item: t('competencyMaps.title') }),
        message: '',
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['competency-maps'] });
      onClose();
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        t('messages.errorUpdating', { item: t('competencyMaps.title') }),
      );
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  // Инициализация формы при открытии модалки
  React.useEffect(() => {
    if (opened) {
      if (editMap) {
        setInputValue(editMap.name);
        setSelectedProfessions((editMap.professions || []).map((p) => p._id));
        if (editMap.grades && editMap.grades.length > 0) {
          const gradeIds = editMap.grades.map((g) => g._id);
          setSelectedGrades(gradeIds);
          setGradesOrder(editMap.grades);
        } else {
          setSelectedGrades([]);
          setGradesOrder([]);
        }
        if (editMap.swimlanes && editMap.swimlanes.length > 0) {
          const swimlaneIds = editMap.swimlanes.map((s) => s._id);
          setSelectedSwimlanes(swimlaneIds);
          setSwimlanesOrder(editMap.swimlanes);
        } else {
          setSelectedSwimlanes([]);
          setSwimlanesOrder([]);
        }
      } else {
        setInputValue('');
        setSelectedProfessions([]);
        setSelectedGrades([]);
        setGradesOrder([]);
        setSelectedSwimlanes([]);
        setSwimlanesOrder([]);
      }
    }
  }, [opened, editMap]);

  // Компетенции, которые после сохранения пропадут с карты: их грейд или дорожку убрали.
  // Связи остаются в карте и вернутся вместе с грейдом/дорожкой.
  const links = (editMap as { competencies?: { gradeId?: string; swimlaneId?: string }[] } | null)
    ?.competencies;
  const hiddenCount = (links ?? []).filter(
    (l) =>
      editMap?.grades.some((g) => g._id === l.gradeId) &&
      editMap?.swimlanes.some((s) => s._id === l.swimlaneId) &&
      !(selectedGrades.includes(l.gradeId!) && selectedSwimlanes.includes(l.swimlaneId!)),
  ).length;

  const handleSave = () => {
    if (inputValue.trim() === '' || selectedProfessions.length === 0) return;
    const gradesToSave = gradesOrder.map((grade) => grade._id);
    const swimlanesToSave = swimlanesOrder.map((swimlane) => swimlane._id);
    if (editMap) {
      editMapMutation.mutate({
        id: editMap._id,
        name: inputValue,
        professions: selectedProfessions,
        grades: gradesToSave,
        swimlanes: swimlanesToSave,
      });
    } else {
      createMapMutation.mutate({
        name: inputValue,
        professions: selectedProfessions,
        grades: gradesToSave,
        swimlanes: swimlanesToSave,
      });
    }
  };

  const handleGradeSelect = (selectedIds: string[]) => {
    setSelectedGrades(selectedIds);
    setGradesOrder(keepOrder(gradesOrder, grades, selectedIds));
  };

  const handleSwimlaneSelect = (selectedIds: string[]) => {
    setSelectedSwimlanes(selectedIds);
    setSwimlanesOrder(keepOrder(swimlanesOrder, swimlanes, selectedIds));
  };

  const moveGradeUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...gradesOrder];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    setGradesOrder(newOrder);

    // Обновляем порядок в MultiSelect
    const newSelectedGrades = newOrder.map((grade) => grade._id);
    setSelectedGrades(newSelectedGrades);
  };

  const moveGradeDown = (index: number) => {
    if (index === gradesOrder.length - 1) return;
    const newOrder = [...gradesOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setGradesOrder(newOrder);

    // Обновляем порядок в MultiSelect
    const newSelectedGrades = newOrder.map((grade) => grade._id);
    setSelectedGrades(newSelectedGrades);
  };

  const moveSwimlaneUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...swimlanesOrder];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    setSwimlanesOrder(newOrder);

    // Обновляем порядок в MultiSelect
    const newSelectedSwimlanes = newOrder.map((swimlane) => swimlane._id);
    setSelectedSwimlanes(newSelectedSwimlanes);
  };

  const moveSwimlaneDown = (index: number) => {
    if (index === swimlanesOrder.length - 1) return;
    const newOrder = [...swimlanesOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setSwimlanesOrder(newOrder);

    // Обновляем порядок в MultiSelect
    const newSelectedSwimlanes = newOrder.map((swimlane) => swimlane._id);
    setSelectedSwimlanes(newSelectedSwimlanes);
  };

  const handleEditGrade = (grade: Grade) => {
    setSelectedGradeForEdit(grade);
    setGradeModalOpen(true);
  };

  const handleEditSwimlane = (swimlane: Swimlane) => {
    setSelectedSwimlaneForEdit(swimlane);
    setSwimlaneModalOpen(true);
  };

  const handleGradeCreated = (gradeId: string, gradeData: Grade) => {
    // Добавляем новый грейд к выбранным
    const newSelectedGrades = [...selectedGrades, gradeId];
    setSelectedGrades(newSelectedGrades);

    // Используем данные из ответа сервера
    const newGrade: Grade = {
      _id: gradeId,
      title: gradeData.title,
      description: gradeData.description,
      qualification: gradeData.qualification,
      position: gradeData.position,
    } as Grade;
    setGradesOrder([...gradesOrder, newGrade]);
  };

  const handleGradeUpdated = (gradeId: string, gradeData: Grade) => {
    // Обновляем грейд в списке
    const updatedGrade: Grade = {
      _id: gradeId,
      title: gradeData.title,
      description: gradeData.description,
      qualification: gradeData.qualification,
      position: gradeData.position,
    } as Grade;

    const updatedGradesOrder = gradesOrder.map((grade) =>
      grade._id === gradeId ? updatedGrade : grade,
    );
    setGradesOrder(updatedGradesOrder);
  };

  const handleSwimlaneCreated = (swimlaneId: string, swimlaneData: Swimlane) => {
    // Добавляем новый свимлейн к выбранным
    const newSelectedSwimlanes = [...selectedSwimlanes, swimlaneId];
    setSelectedSwimlanes(newSelectedSwimlanes);

    // Используем данные из ответа сервера
    const newSwimlane: Swimlane = {
      _id: swimlaneId,
      title: swimlaneData.title,
    };
    setSwimlanesOrder([...swimlanesOrder, newSwimlane]);
  };

  const handleSwimlaneUpdated = (swimlaneId: string, swimlaneData: Swimlane) => {
    // Обновляем свимлейн в списке
    const updatedSwimlane: Swimlane = {
      _id: swimlaneId,
      title: swimlaneData.title,
    };

    const updatedSwimlanesOrder = swimlanesOrder.map((swimlane) =>
      swimlane._id === swimlaneId ? updatedSwimlane : swimlane,
    );
    setSwimlanesOrder(updatedSwimlanesOrder);
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={editMap ? t('competencyMaps.editMap') : t('competencyMaps.addMap')}
        size="lg"
      >
        <Stack>
          <TextInput
            label={t('competencyMaps.name')}
            placeholder={t('competencyMaps.name')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
            onKeyDown={createEnterKeyHandler({ onSubmit: handleSave })}
            required
          />
          <MultiSelect
            label={t('competencyMaps.profession')}
            placeholder={t('competencyMaps.selectProfession')}
            data={professions.map((p: Profession) => ({ value: p._id, label: p.name }))}
            value={selectedProfessions}
            onChange={setSelectedProfessions}
            searchable
            required
          />
          <OrderedMultiSelect
            items={gradesOrder}
            allItems={grades}
            selectedItems={selectedGrades}
            onSelectionChange={handleGradeSelect}
            title={t('competencyMaps.grades')}
            orderTitle={t('competencyMaps.gradesOrder')}
            placeholder={t('competencyMaps.selectGrades')}
            addItemText={t('competencyMaps.addGrade')}
            onMoveUp={moveGradeUp}
            onMoveDown={moveGradeDown}
            onEdit={(item) => handleEditGrade(item as Grade)}
            onAddNew={() => {
              setSelectedGradeForEdit(null);
              setGradeModalOpen(true);
            }}
          />
          <OrderedMultiSelect
            items={swimlanesOrder}
            allItems={swimlanes}
            selectedItems={selectedSwimlanes}
            onSelectionChange={handleSwimlaneSelect}
            title={t('competencyMaps.swimlanes')}
            orderTitle={t('competencyMaps.swimlanesOrder')}
            placeholder={t('competencyMaps.selectSwimlanes')}
            addItemText={t('competencyMaps.addSwimlane')}
            onMoveUp={moveSwimlaneUp}
            onMoveDown={moveSwimlaneDown}
            onEdit={(item) => handleEditSwimlane(item as Swimlane)}
            onAddNew={() => {
              setSelectedSwimlaneForEdit(null);
              setSwimlaneModalOpen(true);
            }}
          />
          {hiddenCount > 0 && (
            <Alert color="orange" variant="light">
              {t('competencyMaps.hiddenOnSave', { count: hiddenCount })}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={onClose} color="gray">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              color="teal"
              disabled={inputValue.trim() === '' || selectedProfessions.length === 0}
            >
              {editMap ? t('common.save') : t('common.add')}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <GradeEditModal
        opened={gradeModalOpen}
        onClose={() => {
          setGradeModalOpen(false);
          setSelectedGradeForEdit(null);
        }}
        grade={selectedGradeForEdit}
        onGradeCreated={handleGradeCreated}
        onGradeUpdated={handleGradeUpdated}
      />
      <SwimlaneEditModal
        opened={swimlaneModalOpen}
        onClose={() => {
          setSwimlaneModalOpen(false);
          setSelectedSwimlaneForEdit(null);
        }}
        swimlane={selectedSwimlaneForEdit}
        onSwimlaneCreated={handleSwimlaneCreated}
        onSwimlaneUpdated={handleSwimlaneUpdated}
      />
    </>
  );
};
